import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { MessageCircle, Loader2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

interface ChatStartButtonProps {
  listingId: string;
  sellerId?: string;
  onChatStarted: (chatId: string) => void;
  onError: (error: string) => void;
}

export function ChatStartButton({
  listingId,
  sellerId,
  onChatStarted,
  onError,
}: ChatStartButtonProps) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);

  const handleStartChat = async () => {
    if (!user) {
      onError("로그인이 필요합니다");
      return;
    }

    if (!sellerId) {
      onError("판매자 정보를 찾을 수 없습니다");
      return;
    }

    setLoading(true);

    try {
      // Get Firebase auth token - REQUIRED
      let token: string;
      try {
        token = await user.getIdToken();
        if (!token || token.trim() === "") {
          throw new Error("Failed to get authentication token");
        }
        console.log("[ChatStartButton] Got ID token (length:", token.length, ")");
      } catch (tokenError: any) {
        console.error("[ChatStartButton] Token error:", tokenError);
        onError("인증 토큰을 가져올 수 없습니다. 다시 로그인해주세요.");
        setLoading(false);
        return;
      }

      // Validate listing ID
      if (!listingId || listingId.trim() === "") {
        onError("상품 ID가 유효하지 않습니다");
        setLoading(false);
        return;
      }

      // Get listing info to find seller_id (Firebase UID)
      const listingRes = await fetch(`/api/market/${listingId}`);
      if (!listingRes.ok) {
        const errorText = await listingRes.text().catch(() => "Unknown error");
        console.error("[ChatStartButton] Listing fetch error:", errorText);
        throw new Error("상품 정보를 불러올 수 없습니다");
      }
      const listing = await listingRes.json();
      const listingData = listing.data || listing;

      // Get seller's Firebase UID from listing
      // Note: listingData.userId should be the Firebase UID
      const sellerFirebaseUid = listingData.userId || sellerId;
      const buyerFirebaseUid = user.uid;

      if (!buyerFirebaseUid || buyerFirebaseUid.trim() === "") {
        onError("사용자 정보를 확인할 수 없습니다");
        setLoading(false);
        return;
      }

      if (sellerFirebaseUid === buyerFirebaseUid) {
        onError("자신의 상품에는 채팅할 수 없습니다");
        setLoading(false);
        return;
      }

      // Start chat
      console.log("[ChatStartButton] Starting chat:", {
        listing_id: listingId,
        buyer_id: buyerFirebaseUid,
        hasToken: !!token,
      });

      const chatRes = await fetch("/api/chat/start", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          listing_id: listingId,
          buyer_id: buyerFirebaseUid,
        }),
      });

      if (!chatRes.ok) {
        const statusText = chatRes.statusText || "Unknown error";
        let errorMessage = `채팅 시작에 실패했습니다 (${chatRes.status})`;

        try {
          const errorData = await chatRes.json();
          errorMessage = errorData.error || errorData.message || errorMessage;

          // Log detailed error information
          console.error("[ChatStartButton] Chat start API error:", {
            status: chatRes.status,
            statusText,
            error: errorData,
            details: errorData.details,
          });

          // Show hint if available
          if (errorData.details?.hint) {
            console.warn("[ChatStartButton] Error hint:", errorData.details.hint);
          }
        } catch (parseError) {
          const errorText = await chatRes.text().catch(() => statusText);
          console.error("[ChatStartButton] Chat start API error (non-JSON):", {
            status: chatRes.status,
            statusText,
            body: errorText,
          });
          errorMessage = errorText || errorMessage;
        }

        throw new Error(errorMessage);
      }

      const chatData = await chatRes.json();
      if (!chatData.chat_id) {
        throw new Error("채팅방 ID를 받지 못했습니다");
      }

      console.log("[ChatStartButton] Chat started successfully:", chatData.chat_id);
      onChatStarted(chatData.chat_id);
    } catch (error: any) {
      console.error("[ChatStartButton] Chat start error:", {
        message: error.message,
        stack: error.stack,
        name: error.name,
      });
      onError(error.message || "채팅 시작에 실패했습니다");
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <Card className="border-blue-200 bg-blue-50 dark:bg-blue-950/20">
        <CardContent className="py-6">
          <p className="text-center text-sm text-muted-foreground mb-4">
            채팅을 시작하려면 로그인이 필요합니다
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-green-200 bg-green-50 dark:bg-green-950/20">
      <CardContent className="py-6">
        <Button
          onClick={handleStartChat}
          disabled={loading}
          className="w-full bg-brand-orange hover:bg-brand-orange/90 text-white font-semibold text-lg py-6 rounded-xl"
          data-testid="button-start-chat"
        >
          {loading ? (
            <>
              <Loader2 className="h-5 w-5 mr-2 animate-spin" />
              채팅 시작 중...
            </>
          ) : (
            <>
              <MessageCircle className="h-5 w-5 mr-2" />
              채팅하기
            </>
          )}
        </Button>
        <p className="text-center text-sm text-muted-foreground mt-3">
          안전한 내장 채팅으로 거래를 진행하세요
        </p>
      </CardContent>
    </Card>
  );
}

